import {
	AddAlarmOutlined,
	DarkModeOutlined,
	DialpadOutlined,
	EventOutlined,
	InfoOutlined,
	LightModeOutlined,
	PhoneCallbackOutlined,
	RefreshOutlined,
	SwapHorizOutlined,
	TouchAppOutlined
} from '@mui/icons-material';
import {
	AppBar,
	Box,
	Button,
	Chip,
	CircularProgress,
	Divider,
	Drawer,
	Fade,
	IconButton,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Popover,
	Toolbar,
	Tooltip,
	Typography,
	keyframes,
	useTheme
} from '@mui/material';
import { generateClient, post } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Cache } from 'aws-amplify/utils';
import { Buffer } from 'buffer';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { UserContext } from '../../../contexts/UserContext';
import { campaignsByTenant, getContact, manualProfileByTenant } from '../../../graphql/queries';
import {
	onTenantCreateCampaign,
	onTenantDeleteCampaign,
	onTenantUpdateCampaign,
} from '../../../graphql/subscriptions';
import { getAgentLastCall } from '../../../tools/cacheService';
import { CallbackWorkspace } from './callbacks';
import { ClickToCall } from '../clickToCall';
import { ManualDialing } from './manualDialing';
import { PreviewDialing } from './previewDialing';
import { ScheduleCallbackDialog } from './scheduleCallback';
import { SkillSwap } from './skillSwap';

const DRAWER_WIDTH = 72;
const APPBAR_HEIGHT = 56;

// ─── Splash / Loading Logo ──────────────────────────────────────────────────────

const fadeInOut = keyframes`
	0%   { opacity: 0; }
	30%  { opacity: 1; }
	70%  { opacity: 1; }
	100% { opacity: 0; }
`;

function SplashLogo() {
	return (
		<Box
			display="flex"
			justifyContent="center"
			alignItems="center"
			height="100vh"
			width="100vw"
			sx={{ bgcolor: 'background.default' }}
		>
			<Box
				component="img"
				src="/logo.svg"
				alt="Velocity"
				sx={{
					width: 160,
					height: 'auto',
					animation: `${fadeInOut} 2s ease-in-out infinite`,
				}}
			/>
		</Box>
	);
}

// ─── Main Workspace ─────────────────────────────────────────────────────────────

const ALL_DIAL_MODES = [
	{ value: 'preview', label: 'Preview', icon: <PhoneCallbackOutlined /> },
	{ value: 'click2call', label: 'Click2Call', icon: <TouchAppOutlined /> },
	{ value: 'manual', label: 'Manual Dialing', icon: <DialpadOutlined /> },
	{ value: 'callback', label: 'Callbacks', icon: <EventOutlined /> },
	{ value: 'skillSwap', label: 'Skill Swap', icon: <SwapHorizOutlined /> },
];

export function Workspace(props) {
	const theme = useTheme();
	const client = generateClient();

	const [tenant, setTenant] = useState('');
	const [dialMode, setDialMode] = useState('');
	const [loading, setLoading] = useState(true);
	let { tenantAuth } = useParams();
	let [tenantsList, setTenantsList] = useState([]);
	const [authState, setAuthState] = useState(null);
	const [skillsAnchor, setSkillsAnchor] = useState(null);
	const [tenantAnchor, setTenantAnchor] = useState(null);
	const [refreshingSkills, setRefreshingSkills] = useState(false);
	const [lastSkillSync, setLastSkillSync] = useState(null);

	/** The active contact from the current dialing mode — used for Schedule Callback */
	const [activeContact, setActiveContact] = useState(null);
	const activeContactRef = useRef(null);
	const [scheduleCallbackOpen, setScheduleCallbackOpen] = useState(false);
	const [callbackRefreshKey, setCallbackRefreshKey] = useState(0);
	const [campaigns, setCampaigns] = useState([]);
	const [manualProfiles, setManualProfiles] = useState([]);
	const [loadingTabs, setLoadingTabs] = useState(false);

	/** Ensures the splash logo plays at least one full fade-in/fade-out cycle */
	const [splashComplete, setSplashComplete] = useState(false);

	useEffect(() => {
		const timer = setTimeout(() => setSplashComplete(true), 2000);
		return () => clearTimeout(timer);
	}, []);

	/**
	 * Callback for child mode components to report their active contact/campaign context.
	 * Shape: { id, contactId, firstName, lastName, phone, cell, campaignId, segmentId, skillId }
	 */
	const handleActiveContactChange = useCallback((contact) => {
		setActiveContact(contact);
		activeContactRef.current = contact;
	}, []);

	// Load campaigns and manual profiles when authenticated to determine tab visibility
	useEffect(() => {
		if (!authState) return;
		loadCampaignsAndProfiles();
	}, [authState?.agentId]);

	async function loadCampaignsAndProfiles() {
		setLoadingTabs(true);
		try {
			const idToken = (await fetchAuthSession()).tokens.idToken;
			const tenantId = idToken.payload['custom:tenant'];

			const [campaignsResponse, manualProfilesResponse] = await Promise.all([
				client.graphql({
					query: campaignsByTenant,
					variables: {
						tenant: tenantId,
						filter: { status: { eq: 'Active' } },
						limit: 1000,
					},
				}),
				client.graphql({
					query: manualProfileByTenant,
					variables: {
						tenant: tenantId,
						limit: 1000,
					},
				}),
			]);

			setCampaigns(campaignsResponse.data.campaignsByTenant?.items ?? []);
			setManualProfiles(manualProfilesResponse.data.manualProfileByTenant?.items ?? []);
		} catch (error) {
			console.error('Error loading campaigns/profiles for tab visibility', error);
		}
		setLoadingTabs(false);
	}

	/** Re-fetch only campaigns (lightweight refresh triggered by subscriptions) */
	async function refreshCampaigns() {
		try {
			const idToken = (await fetchAuthSession()).tokens.idToken;
			const tenantId = idToken.payload['custom:tenant'];

			const campaignsResponse = await client.graphql({
				query: campaignsByTenant,
				variables: {
					tenant: tenantId,
					filter: { status: { eq: 'Active' } },
					limit: 1000,
				},
			});

			setCampaigns(campaignsResponse.data.campaignsByTenant?.items ?? []);
		} catch (error) {
			console.error('Error refreshing campaigns', error);
		}
	}

	// Subscribe to real-time campaign changes (create, update, delete)
	useEffect(() => {
		if (!authState) return;

		let tenantId;
		const subscriptions = [];

		(async () => {
			try {
				const idToken = (await fetchAuthSession()).tokens.idToken;
				tenantId = idToken.payload['custom:tenant'];
			} catch (error) {
				console.error('Error fetching tenant for campaign subscriptions', error);
				return;
			}

			// ─── New Campaign Created ───────────────────────────────────────
			const createSub = client
				.graphql({
					query: onTenantCreateCampaign,
					variables: { tenant: tenantId },
				})
				.subscribe({
					next: (data) => {
						const newCampaign = data.data.onTenantCreateCampaign;
						if (newCampaign.status !== 'Active') return;

						const agentSkills = authState.agentActiveSkillIds || [];
						const hasAccess =
							!agentSkills.length || agentSkills.includes(newCampaign.skillId);

						if (hasAccess) {
							enqueueSnackbar(
								`New campaign available: ${newCampaign.name}`,
								{ variant: 'info', autoHideDuration: 5000 },
							);
							refreshCampaigns();
						}
					},
					error: (err) => console.error('Campaign create subscription error', err),
				});
			subscriptions.push(createSub);

			// ─── Campaign Updated ───────────────────────────────────────────
			const updateSub = client
				.graphql({
					query: onTenantUpdateCampaign,
					variables: { tenant: tenantId },
				})
				.subscribe({
					next: (data) => {
						const updated = data.data.onTenantUpdateCampaign;
						const agentSkills = authState.agentActiveSkillIds || [];

						// Only process campaigns that match the agent's assigned skills
						const hasAccess = !agentSkills.length || agentSkills.includes(updated.skillId);

						// Campaign ended, completed, or reset — remove it from active list
						if (updated.status === 'Completed' || updated.status === 'Expired' || updated.status === 'Pending') {
							setCampaigns((prev) => {
								const existed = prev.find((c) => c.id === updated.id);
								if (!existed) return prev;

								// Only notify if the agent has access to this campaign's skill
								if (hasAccess) {
									if (activeContactRef.current?.campaignId === updated.id) {
										const message =
											updated.status === 'Completed' || updated.status === 'Expired'
												? `Campaign "${updated.name}" has ended. Please wrap up your current contact.`
												: `Campaign "${updated.name}" has been reset.`;
										enqueueSnackbar(message, { variant: 'warning', autoHideDuration: 8000 });
									} else {
										enqueueSnackbar(
											`Campaign "${updated.name}" is no longer active`,
											{ variant: 'info', autoHideDuration: 5000 },
										);
									}
								}
								return prev.filter((c) => c.id !== updated.id);
							});
							return;
						}

						// Campaign is now Active (started or restarted) — full refresh to get complete data
						if (updated.status === 'Active') {
							setCampaigns((prev) => {
								const existed = prev.find((c) => c.id === updated.id);

								// Only notify on meaningful changes for campaigns the agent has access to
								if (hasAccess && !existed) {
									// New campaign became available — status change
									enqueueSnackbar(
										`New campaign available: ${updated.name}`,
										{ variant: 'info', autoHideDuration: 5000 },
									);
								} else if (hasAccess && existed) {
									// Only notify if profile (dialing mode) actually changed
									const profileChanged =
										existed.profile?.previewDialing !== updated.profile?.previewDialing ||
										existed.profile?.clickToCall !== updated.profile?.clickToCall;
									if (profileChanged) {
										enqueueSnackbar(
											`Campaign "${updated.name}" dialing options updated`,
											{ variant: 'info', autoHideDuration: 5000 },
										);
									}
								} else if (!hasAccess && existed) {
									// Skill changed away from agent — effectively a status change for them
									enqueueSnackbar(
										`Campaign "${updated.name}" is no longer available for your skills`,
										{ variant: 'info', autoHideDuration: 5000 },
									);
								}
								// No notification for campaigns without access that didn't previously exist
								return prev;
							});

							// Re-fetch to get complete campaign data (including callbackConfigurationId)
							refreshCampaigns();
							return;
						}

						// Paused — only notify and update if agent has access
						if (updated.status === 'Paused') {
							setCampaigns((prev) => {
								const existed = prev.find((c) => c.id === updated.id);
								if (existed && hasAccess) {
									enqueueSnackbar(
										`Campaign "${updated.name}" has been paused`,
										{ variant: 'info', autoHideDuration: 5000 },
									);
									return prev.map((c) => (c.id === updated.id ? { ...existed, status: 'Paused' } : c));
								}
								return prev;
							});
							return;
						}

						// Any other update (no status change, no profile change) — just refresh
						// campaign data silently. No toast needed.
						if (hasAccess) {
							refreshCampaigns();
						}
					},
					error: (err) => console.error('Campaign update subscription error', err),
				});
			subscriptions.push(updateSub);

			// ─── Campaign Deleted ───────────────────────────────────────────
			const deleteSub = client
				.graphql({
					query: onTenantDeleteCampaign,
					variables: { tenant: tenantId },
				})
				.subscribe({
					next: (data) => {
						const deleted = data.data.onTenantDeleteCampaign;
						const agentSkills = authState.agentActiveSkillIds || [];
						const hasAccess = !agentSkills.length || agentSkills.includes(deleted.skillId);

						setCampaigns((prev) => {
							const existed = prev.some((c) => c.id === deleted.id);
							if (!existed) return prev;

							// Only notify if the agent has access to this campaign's skill
							if (hasAccess) {
								if (activeContactRef.current?.campaignId === deleted.id) {
									enqueueSnackbar(
										`Campaign "${deleted.name}" has been removed. Please wrap up your current contact.`,
										{ variant: 'warning', autoHideDuration: 8000 },
									);
								} else {
									enqueueSnackbar(
										`Campaign "${deleted.name}" removed`,
										{ variant: 'info', autoHideDuration: 5000 },
									);
								}
							}
							return prev.filter((c) => c.id !== deleted.id);
						});
					},
					error: (err) => console.error('Campaign delete subscription error', err),
				});
			subscriptions.push(deleteSub);
		})();

		return () => {
			subscriptions.forEach((sub) => sub.unsubscribe());
		};
	}, [authState?.agentId, authState?.agentActiveSkillIds]);

	/** Check if the active contact's campaign has a callback configuration */
	const activeContactCampaign = activeContact
		? campaigns.find((c) =>
			(activeContact.campaignId && c.id === activeContact.campaignId) ||
			(activeContact.skillId && c.skillId === activeContact.skillId && c.callbackConfigurationId)
		)
		: null;
	const callbackEnabled = Boolean(activeContactCampaign?.callbackConfigurationId);

	/** On the callbacks tab, allow scheduling even without an active contact from the dialer */
	const isOnCallbackTab = dialMode === 'callback';

	/**
	 * Fetches the agent's last active call from the cache and opens the Schedule Callback dialog.
	 * Used when the agent clicks Schedule Callback without a current active contact in the dialer.
	 */
	async function fetchLastActiveCallAndSchedule() {
		// If we already have an active contact with valid data, just open the dialog
		if (activeContact && activeContact.hasCalledOrOnCall && callbackEnabled) {
			setScheduleCallbackOpen(true);
			return;
		}

		try {
			// Fetch agent's last call data from cache
			const agentData = await getAgentLastCall(authState.agentId, authState.userContext.apiKey);

			if (!agentData?.contactId) {
				enqueueSnackbar('No last call data found.', { variant: 'info' });
				return;
			}

			// Find matching campaign for this last call's skill
			const matchingCampaign = campaigns.find(
				(c) => c.skillId === agentData.skillId && c.callbackConfigurationId,
			);
			if (!matchingCampaign) {
				enqueueSnackbar('No callback-enabled campaign found for the last call.', { variant: 'info' });
				return;
			}

			// Load the full contact record
			const contactResponse = await client.graphql({
				query: getContact,
				variables: { id: agentData.contactId },
			});
			const contact = contactResponse.data.getContact;
			if (!contact) {
				enqueueSnackbar('Could not load contact for last call.', { variant: 'info' });
				return;
			}

			if (contact.customFields && typeof contact.customFields === 'string') {
				contact.customFields = JSON.parse(contact.customFields);
			}

			// Set active contact from the last call data
			const lastCallContact = {
				id: contact.id,
				contactId: contact.id,
				firstName: contact.firstName,
				lastName: contact.lastName,
				phone: contact.phone,
				cell: contact.cell,
				externalId: contact.externalId || '',
				campaignId: matchingCampaign.id,
				segmentId: agentData.segmentId || contact.segmentId || '',
				skillId: matchingCampaign.skillId || '',
				lastDialedNumber: agentData.lastDialedNumber || contact.phone || contact.cell || '',
				hasCalledOrOnCall: true,
				isCallActive: false,
			};

			setActiveContact(lastCallContact);
			activeContactRef.current = lastCallContact;
			setScheduleCallbackOpen(true);
		} catch (error) {
			console.error('Error fetching last active call for callback scheduling', error);
			enqueueSnackbar('Error loading last call data.', { variant: 'warning' });
		}
	}

	// Derive tab visibility from agent's active skills, campaigns, and manual profiles
	const activeSkills = authState?.agentActiveSkillIds || [];

	const agentCampaigns = campaigns.filter(
		(campaign) => !activeSkills.length || activeSkills.includes(campaign.skillId),
	);

	const hasPreviewCampaigns = agentCampaigns.some((c) => c.profile?.previewDialing);
	const hasClickToCallCampaigns = agentCampaigns.some((c) => c.profile?.clickToCall);
	const hasCallbackCampaigns = agentCampaigns.some((c) => c.callbackConfigurationId);
	const hasManualProfiles = manualProfiles.some(
		(profile) => !activeSkills.length || activeSkills.includes(profile.skills),
	);

	const availableModes = ALL_DIAL_MODES.filter((mode) => {
		if (!authState) return false;

		switch (mode.value) {
			case 'preview':
				return hasPreviewCampaigns;
			case 'click2call':
				return hasClickToCallCampaigns;
			case 'manual':
				return hasManualProfiles;
			case 'callback':
				return hasCallbackCampaigns;
			case 'skillSwap':
				return tenant?.skillSwap?.active === true;
			default:
				return false;
		}
	});

	useEffect(() => {
		loadTenantOptions();
	}, [tenantAuth]);

	// Content transition state
	const [contentVisible, setContentVisible] = useState(true);
	const [renderedMode, setRenderedMode] = useState('');

	// Animate content on mode change
	useEffect(() => {
		if (dialMode === renderedMode) return;

		// Block navigation if a call is actively in progress on preview/manual/callback
		if (activeContactRef.current?.isCallActive && (renderedMode === 'preview' || renderedMode === 'manual' || renderedMode === 'callback')) {
			enqueueSnackbar('Cannot switch modes while a call is in progress', { variant: 'warning' });
			setDialMode(renderedMode);
			return;
		}

		// Fade out, swap content, then fade in
		setContentVisible(false);
		const timeout = setTimeout(() => {
			setRenderedMode(dialMode);
			setActiveContact(null);
			activeContactRef.current = null;
			setContentVisible(true);
		}, 200); // matches fade-out duration

		return () => clearTimeout(timeout);
	}, [dialMode]);

	function loadTenantOptions() {
		try {
			const decodedTenantInfo = Buffer.from(tenantAuth, 'base64');
			const parsedTenantInfo = JSON.parse(decodedTenantInfo);
			setTenantsList(parsedTenantInfo);
			if (parsedTenantInfo.length === 1) {
				setTenant(parsedTenantInfo[0]);
			}
		} catch (error) {
			console.error('Error parsing tenant auth', error);
			enqueueSnackbar('Failed to load tenant information', { variant: 'error' });
		}
		setLoading(false);
	}

	function handleAuthenticated({ agentId, agentSkillIds, agentActiveSkillIds, agentSkillNames, agentName, userContext }) {
		setAuthState({
			agentId,
			agentSkillIds,
			agentActiveSkillIds: agentActiveSkillIds || agentSkillIds,
			agentSkillNames: agentSkillNames || {},
			agentName: agentName || '',
			userContext,
		});
		setLastSkillSync(new Date());
	}

	async function refreshAgentSkills() {
		if (!authState) return;
		setRefreshingSkills(true);
		try {
			const session = await fetchAuthSession();
			const response = await post({
				apiName: 'cdyxoutreach',
				path: '/approver/c2c-generate',
				options: {
					headers: {
						Authorization: `Bearer ${session.tokens.idToken}`,
						'x-api-key': authState.userContext.apiKey,
					},
					body: {
						agentId: authState.agentId,
						refreshOnly: true,
						hideInbound: tenant?.skillSwap?.hideInbound ?? true,
						hideOutbound: tenant?.skillSwap?.hideOutbound ?? false,
					},
				},
			}).response;
			const data = await response.body.json();
			if (data.skills) {
				const allSkillIds = data.skills.map((skill) =>
					typeof skill === 'object' ? String(skill.skillId) : String(skill)
				);
				const activeSkillIds = data.activeSkills
					? data.activeSkills.map((skill) =>
						typeof skill === 'object' ? String(skill.skillId) : String(skill)
					)
					: allSkillIds;
				const skillNameMap = {};
				for (const skill of data.skills) {
					if (typeof skill === 'object') {
						skillNameMap[String(skill.skillId)] = skill.skillName || String(skill.skillId);
					}
				}
				setAuthState((prev) => ({
					...prev,
					agentSkillIds: allSkillIds,
					agentActiveSkillIds: activeSkillIds,
					agentSkillNames: { ...prev.agentSkillNames, ...skillNameMap },
				}));
				setLastSkillSync(new Date());

				// Update cache so page reloads get fresh data
				const expireTime = new Date().getTime() + 3600000;
				await Cache.setItem('niceAgentSkills', allSkillIds, { expires: expireTime });
				await Cache.setItem('niceAgentActiveSkills', activeSkillIds, { expires: expireTime });
				if (Object.keys(skillNameMap).length > 0) {
					await Cache.setItem('niceAgentSkillNames', skillNameMap, { expires: expireTime });
				}
			}
		} catch (error) {
			console.error('Error refreshing agent skills', error);
		}
		setRefreshingSkills(false);
	}

	// Auto-refresh skills every 10 minutes
	useEffect(() => {
		if (!authState) return;
		const interval = setInterval(() => {
			refreshAgentSkills();
		}, 10 * 60 * 1000);
		return () => clearInterval(interval);
	}, [authState?.agentId]);

	if (!splashComplete || loading) {
		return <SplashLogo />;
	}

	if (authState && loadingTabs) {
		return <SplashLogo />;
	}

	return (
		<Box sx={{ display: 'flex', height: '100vh' }}>
			{/* Sidenav Drawer */}
			<Drawer
				variant="permanent"
				sx={{
					width: DRAWER_WIDTH,
					flexShrink: 0,
					'& .MuiDrawer-paper': {
						width: DRAWER_WIDTH,
						boxSizing: 'border-box',
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						overflow: 'hidden',
					},
				}}
			>
				{/* Velocity Logo */}
				<Box sx={{ height: APPBAR_HEIGHT, px: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
					<img src="/logo.svg" alt="Velocity Logo" style={{ width: '36px', height: 'auto' }} />
				</Box>

				{/* Mode navigation */}
				<List sx={{ width: '100%', px: 0, mt: 1 }}>
					{availableModes.map((mode) => (
						<ListItem key={mode.value} disablePadding>
							<Tooltip title={mode.label} placement="right">
								<ListItemButton
									selected={dialMode === mode.value}
									onClick={() => { if (tenant && authState) setDialMode(mode.value); }}
									disabled={!tenant || !authState}
									sx={{ flexDirection: 'column', alignItems: 'center', py: 1.5, minHeight: 56 }}
								>
									<ListItemIcon
										sx={{
											minWidth: 'unset',
											justifyContent: 'center',
											color: dialMode === mode.value ? 'primary.main' : 'text.secondary',
										}}
									>
										{mode.icon}
									</ListItemIcon>
									<ListItemText
										primary={mode.label}
										primaryTypographyProps={{
											variant: 'caption',
											fontSize: '0.6rem',
											textAlign: 'center',
											lineHeight: 1.2,
											mt: 0.5,
											color: dialMode === mode.value ? 'primary.main' : 'text.secondary',
										}}
									/>
								</ListItemButton>
							</Tooltip>
						</ListItem>
					))}
				</List>

				{/* Agent info — pushed to bottom */}
				<Box sx={{ mt: 'auto', pb: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
					{authState && (
						<Tooltip title="Agent Skills" placement="right">
							<IconButton onClick={(e) => setSkillsAnchor(e.currentTarget)}>
								<InfoOutlined />
							</IconButton>
						</Tooltip>
					)}
				</Box>

				{/* Agent Skills Popover */}
				<Popover
					open={Boolean(skillsAnchor)}
					anchorEl={skillsAnchor}
					onClose={() => setSkillsAnchor(null)}
					anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
					transformOrigin={{ vertical: 'center', horizontal: 'left' }}
				>
					<Box sx={{ p: 2, minWidth: 200, maxWidth: 320 }}>
						<Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
							<Typography variant="subtitle2">Agent Skills</Typography>
							<Tooltip title="Refresh Skills">
								<IconButton size="small" onClick={refreshAgentSkills} disabled={refreshingSkills}>
									{refreshingSkills ? <CircularProgress size={16} /> : <RefreshOutlined fontSize="small" />}
								</IconButton>
							</Tooltip>
						</Box>
						<Divider sx={{ mb: 1 }} />
						{authState?.agentSkillIds?.length > 0 ? (
							<Box display="flex" flexWrap="wrap" gap={0.5}>
								{authState.agentSkillIds.map((skillId) => (
									<Chip
										key={skillId}
										label={skillId}
										size="small"
										variant="outlined"
										color={authState.agentActiveSkillIds?.includes(skillId) ? 'primary' : 'default'}
										sx={!authState.agentActiveSkillIds?.includes(skillId) ? { opacity: 0.5 } : {}}
									/>
								))}
							</Box>
						) : (
							<Typography variant="body2" color="text.secondary">No skills available</Typography>
						)}
						<Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
							Active skills are highlighted. Inactive skills are dimmed.
						</Typography>
						{authState?.agentId && (
							<Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
								Agent ID: {authState.agentId}
							</Typography>
						)}
						{lastSkillSync && (
							<Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
								Last synced: {lastSkillSync.toLocaleTimeString()}
							</Typography>
						)}
					</Box>
				</Popover>
			</Drawer>

			{/* Main content column */}
			<Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', height: '100vh' }}>
				{/* Top AppBar */}
				<AppBar
					position="static"
					elevation={0}
					sx={{
						bgcolor: 'background.paper',
						borderBottom: 1,
						borderColor: 'divider',
						minHeight: APPBAR_HEIGHT,
					}}
				>
					<Toolbar variant="dense" sx={{ minHeight: APPBAR_HEIGHT, gap: 1 }}>

						<Box sx={{ flexGrow: 1 }} />

						{/* Schedule Callback button — only shown when NOT on the callback tab's list view.
						    On the callback tab it's hidden unless the agent is inside the ContactPreview (has an active contact). */}
						{authState && !(renderedMode === 'callback' && (!activeContact || !activeContact.hasCalledOrOnCall)) && (
							<Tooltip title={
								!activeContact
									? 'Work a valid contact to enable scheduling.'
									: !activeContact.hasCalledOrOnCall
										? 'Make a call or be on a call to schedule a callback'
										: !callbackEnabled
											? 'Callbacks are not enabled for this campaign'
											: (renderedMode === 'manual' && !activeContact.isLastCallContact)
												? 'Callback scheduling is only available for your last assigned contact'
												: 'Schedule a callback for the active contact'
							}>
								<span>
									<Button
										size="small"
										variant="outlined"
										startIcon={<AddAlarmOutlined />}
										onClick={() => setScheduleCallbackOpen(true)}
										disabled={
											!activeContact ||
											!activeContact.hasCalledOrOnCall ||
											!callbackEnabled ||
											(renderedMode === 'manual' && !activeContact.isLastCallContact)
										}
										sx={{ borderRadius: '7px' }}
									>
										Schedule Callback
									</Button>
								</span>
							</Tooltip>
						)}

						{/* Theme toggle */}
						<Tooltip title={theme.palette.mode === 'dark' ? 'Light Mode' : 'Dark Mode'}>
							<IconButton
								size="small"
								onClick={() => {
									const newMode = theme.palette.mode === 'dark' ? 'light' : 'dark';
									props.setThemeMode(newMode);
									localStorage.setItem('previouslySelectedTheme', newMode);
								}}
							>
								{theme.palette.mode === 'dark' ? <LightModeOutlined fontSize="small" /> : <DarkModeOutlined fontSize="small" />}
							</IconButton>
						</Tooltip>

						{/* Tenant Switcher */}
						{tenantsList?.length > 1 && (
							<>
								<Tooltip title={tenant ? tenant.name : 'Select Tenant'} placement="bottom">
									<IconButton
										onClick={(e) => setTenantAnchor(e.currentTarget)}
										size="small"
										sx={{
											borderRadius: '8px',
											p: 0.5,
											'&:hover': { bgcolor: 'action.hover' },
										}}
									>
										<Box
											sx={{
												width: 24,
												height: 24,
												borderRadius: '6px',
												bgcolor: tenant ? 'primary.main' : 'action.disabledBackground',
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												fontSize: '0.7rem',
												fontWeight: 700,
												color: tenant ? 'primary.contrastText' : 'text.secondary',
											}}
										>
											{tenant ? tenant.name?.charAt(0).toUpperCase() : '?'}
										</Box>
									</IconButton>
								</Tooltip>
								<Menu
									anchorEl={tenantAnchor}
									open={Boolean(tenantAnchor)}
									onClose={() => setTenantAnchor(null)}
									PaperProps={{
										elevation: 3,
										sx: {
											mt: 1,
											minWidth: 200,
											borderRadius: '10px',
											border: 1,
											borderColor: 'divider',
											'& .MuiMenuItem-root': {
												borderRadius: '6px',
												mx: 0.5,
												my: 0.25,
											},
										},
									}}
									transformOrigin={{ horizontal: 'right', vertical: 'top' }}
									anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
								>
									<Box sx={{ px: 1.5, py: 1 }}>
										<Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
											Switch Tenant
										</Typography>
									</Box>
									{tenantsList.map((t) => (
										<MenuItem
											key={t.name}
											selected={tenant?.name === t.name}
											onClick={() => {
												setTenant(t);
												setTenantAnchor(null);
											}}
											sx={{ gap: 1.5, py: 1 }}
										>
											<Box
												sx={{
													width: 26,
													height: 26,
													borderRadius: '6px',
													bgcolor: tenant?.name === t.name ? 'primary.main' : 'action.selected',
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
													fontSize: '0.75rem',
													fontWeight: 700,
													color: tenant?.name === t.name ? 'primary.contrastText' : 'text.primary',
												}}
											>
												{t.name?.charAt(0).toUpperCase()}
											</Box>
											<Typography variant="body2" fontWeight={tenant?.name === t.name ? 600 : 400}>
												{t.name}
											</Typography>
										</MenuItem>
									))}
								</Menu>
							</>
						)}
					</Toolbar>
				</AppBar>

				{/* Content area below AppBar */}
				<Box
					component="main"
					sx={{
						flexGrow: 1,
						overflow: 'auto',
						position: 'relative',
						bgcolor: 'background.default',
					}}
				>
					{/* ClickToCall handles auth + CCI connection + click2call mode */}
					{tenant && (
						<Box
							sx={{
								position: authState && renderedMode !== 'click2call' ? 'absolute' : 'relative',
								inset: 0,
								zIndex: authState && renderedMode !== 'click2call' ? -1 : 1,
								height: !authState || renderedMode === 'click2call' ? '100%' : 0,
								overflow: authState && renderedMode !== 'click2call' ? 'hidden' : 'visible',
							}}
						>
							<ClickToCall
								setThemeMode={props.setThemeMode}
								setDialMode={setDialMode}
								dialMode={dialMode}
								velocityAuth={tenant}
								onAuthenticated={handleAuthenticated}
							/>
						</Box>
					)}

					{/* Placeholder when no tenant selected */}
					{!tenant && tenantsList?.length > 1 && (
						<Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="100%" gap={2}>
							<Box
								sx={{
									width: 48,
									height: 48,
									borderRadius: '12px',
									bgcolor: 'action.selected',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
								}}
							>
								<SwapHorizOutlined sx={{ fontSize: 28, color: 'text.secondary' }} />
							</Box>
							<Typography variant="h6" color="text.primary" fontWeight={500}>
								Choose your workspace
							</Typography>
							<Typography variant="body2" color="text.secondary">
								Select a tenant to get started
							</Typography>
							<Box display="flex" gap={1} flexWrap="wrap" justifyContent="center" sx={{ mt: 1 }}>
								{tenantsList.map((t) => (
									<Button
										key={t.name}
										variant="outlined"
										onClick={() => setTenant(t)}
										sx={{
											textTransform: 'none',
											borderRadius: '10px',
											px: 2.5,
											py: 1,
											borderColor: 'divider',
											'&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
										}}
										startIcon={
											<Box
												sx={{
													width: 24,
													height: 24,
													borderRadius: '6px',
													bgcolor: 'primary.main',
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
													fontSize: '0.7rem',
													fontWeight: 700,
													color: 'primary.contrastText',
												}}
											>
												{t.name?.charAt(0).toUpperCase()}
											</Box>
										}
									>
										{t.name}
									</Button>
								))}
							</Box>
						</Box>
					)}

					{/* Placeholder when authenticated but no mode selected */}
					{authState && !renderedMode && availableModes.length > 0 && (
						<Fade in={contentVisible} timeout={250}>
							<Box display="flex" justifyContent="center" alignItems="center" height="100%">
								<Typography variant="body1" color="text.secondary">
									Select a dialing mode from the sidebar
								</Typography>
							</Box>
						</Fade>
					)}

					{/* No available modes — likely no skills assigned */}
					{authState && !renderedMode && availableModes.length === 0 && !loadingTabs && (
						<Fade in={contentVisible} timeout={250}>
							<Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="100%" gap={2} px={3}>
								<InfoOutlined sx={{ fontSize: 48, color: 'text.disabled' }} />
								<Typography variant="h6" color="text.primary" fontWeight={500} textAlign="center">
									No dialing modes available
								</Typography>
								<Typography variant="body2" color="text.secondary" textAlign="center" maxWidth={400}>
									{(!authState.agentActiveSkillIds || authState.agentActiveSkillIds.length === 0)
										? 'You don\'t have any active skills assigned to your account. Please contact your administrator to assign you the appropriate skills.'
										: 'There are no active campaigns configured for your assigned skills. Please contact your administrator.'}
								</Typography>
							</Box>
						</Fade>
					)}

					{/* === Mode rendering with fade transition === */}
					<Fade in={contentVisible} timeout={250}>
						<Box sx={{ height: renderedMode && renderedMode !== 'click2call' ? '100%' : 0, overflow: renderedMode && renderedMode !== 'click2call' ? 'visible' : 'hidden' }}>
							{authState && renderedMode === 'preview' && (
								<UserContext.Provider value={authState.userContext}>
									<PreviewDialing
										agentId={authState.agentId}
										agentSkillIds={authState.agentActiveSkillIds}
										campaigns={campaigns}
										setDialMode={setDialMode}
										setThemeMode={props.setThemeMode}
										onActiveContactChange={handleActiveContactChange}
									/>
								</UserContext.Provider>
							)}

							{authState && renderedMode === 'manual' && (
								<UserContext.Provider value={authState.userContext}>
									<ManualDialing
										agentId={authState.agentId}
										agentSkillIds={authState.agentActiveSkillIds}
										campaigns={campaigns}
										setDialMode={setDialMode}
										setThemeMode={props.setThemeMode}
										onActiveContactChange={handleActiveContactChange}
									/>
								</UserContext.Provider>
							)}

							{authState && renderedMode === 'callback' && (
								<UserContext.Provider value={authState.userContext}>
									<CallbackWorkspace
										agentId={authState.agentId}
										agentSkillIds={authState.agentActiveSkillIds}
										campaigns={campaigns}
										onActiveContactChange={handleActiveContactChange}
										externalRefreshKey={callbackRefreshKey}
									/>
								</UserContext.Provider>
							)}

							{authState && renderedMode === 'skillSwap' && tenant?.skillSwap?.active && (
								<UserContext.Provider value={authState.userContext}>
									<SkillSwap
										agentId={authState.agentId}
										allSkillIds={authState.agentSkillIds}
										activeSkillIds={authState.agentActiveSkillIds}
										skillNames={authState.agentSkillNames}
										skillSwapConfig={tenant.skillSwap}
										onSkillsUpdated={(newActiveSkillIds) => {
											setAuthState((prev) => ({
												...prev,
												agentActiveSkillIds: newActiveSkillIds,
											}));
											const expireTime = new Date().getTime() + 3600000;
											Cache.setItem('niceAgentActiveSkills', newActiveSkillIds, { expires: expireTime });
										}}
									/>
								</UserContext.Provider>
							)}
						</Box>
					</Fade>
				</Box>
			</Box>

			{/* Schedule Callback Dialog */}
			<ScheduleCallbackDialog
				open={scheduleCallbackOpen}
				onClose={(scheduled) => {
					setScheduleCallbackOpen(false);
					if (scheduled === true) {
						setCallbackRefreshKey((k) => k + 1);
					}
				}}
				activeContact={activeContact}
				agentId={authState?.agentId}
				agentName={authState?.agentName}
				apiKey={authState?.userContext?.apiKey}
			/>
		</Box>
	);
}
